import { Button } from "@ascleo/ui/components/ui/button";
import {
  EligibleContactType,
  eligibleContactKind,
} from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
import { ContactAssertion } from "@ascleo/frontdesk-contracts/patient-info/submit";
import type { ReviewContact } from "@ascleo/frontdesk-contracts/patient-info/review";
import { useForm, useStore } from "@tanstack/react-form";
import { Schema } from "effect";
import {
  Mail,
  Phone,
  Plus,
  RotateCcw,
  Smartphone,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { KioskTextField } from "../../../../../../components/kiosk-field";
import { CheckInChoiceGroup } from "../check-in-choice-group";
import { maskValue } from "./masking";
import { ProtectedProposalRow } from "./ProtectedProposalRow";
import type { PendingAddition } from "./staged-changes";
import type { ReviewForm } from "./use-review-form";

const ADDABLE_TYPES: readonly EligibleContactType[] = [
  "MOBILEPRIV",
  "PHONEPRIV",
  "EMAILPRIV",
  "MOBILEJOB",
  "PHONEJOB",
  "EMAILJOB",
];

/**
 * Intent icon of a contact type (FI-37): mobile, landline or e-mail.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const Icon = contactTypeIcon("MOBILEPRIV"); // Smartphone
 * ```
 */
export function contactTypeIcon(type: EligibleContactType): LucideIcon {
  if (eligibleContactKind(type) === "email") return Mail;
  return type === "MOBILEPRIV" || type === "MOBILEJOB" ? Smartphone : Phone;
}

function inputModeFor(type: EligibleContactType): "email" | "tel" {
  return eligibleContactKind(type) === "email" ? "email" : "tel";
}

/**
 * The inline editor for one contact-bearing category.
 *
 * Opening it never leaves the check sheet: the card grows in place, so the patient keeps the rest
 * of their data in view while changing one number. Editing is always reversible until the single
 * final commit. Every field is a kiosk field with the type as its label and a type icon; every
 * action is a labelled 56px button (KC-58).
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <ContactCategoryEditor
 *   category="email"
 *   contacts={contacts}
 *   form={form}
 *   deleted={new Set()}
 *   additions={[]}
 *   onToggleDelete={() => {}}
 *   onAdd={() => {}}
 *   onDiscardAddition={() => {}}
 *   onDiscardProposal={() => {}}
 * />
 * ```
 */
export function ContactCategoryEditor({
  category,
  contacts,
  form,
  deleted,
  additions,
  onToggleDelete,
  onAdd,
  onDiscardAddition,
  onDiscardProposal,
}: {
  category: "phone" | "email";
  contacts: readonly ReviewContact[];
  form: ReviewForm;
  deleted: ReadonlySet<string>;
  additions: readonly PendingAddition[];
  onToggleDelete: (itemId: string) => void;
  onAdd: (type: EligibleContactType, value: string) => void;
  onDiscardAddition: (clientItemId: string) => void;
  onDiscardProposal: (itemId: string) => void;
}) {
  const { t } = useTranslation();
  const ordinary = contacts.filter((contact) => contact.protection === undefined);
  const guarded = contacts.filter((contact) => contact.protection !== undefined);
  const invalidKey =
    category === "phone"
      ? "self-service.check-in.patient-info.review.invalid-phone"
      : "self-service.check-in.patient-info.review.invalid-email";

  return (
    <div className="mt-2 flex min-w-0 flex-col gap-4" data-testid="patient-info-category-editor">
      {ordinary.map((contact) =>
        deleted.has(contact.itemId) ? (
          <StagedRemovalRow
            key={contact.itemId}
            contact={contact}
            onUndo={() => onToggleDelete(contact.itemId)}
          />
        ) : (
          <form.Field
            key={contact.itemId}
            name={contact.itemId}
            validators={{ onChange: Schema.toStandardSchemaV1(ContactAssertion) }}
          >
            {(field) => (
              <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-start">
                <div className="min-w-0 flex-1">
                  <KioskTextField
                    id={`patient-info-contact-${contact.itemId}`}
                    testId="patient-info-contact-input"
                    errorTestId="patient-info-contact-error"
                    label={t(`self-service.check-in.patient-info.review.type.${contact.type}`)}
                    icon={contactTypeIcon(contact.type)}
                    type={inputModeFor(contact.type) === "email" ? "email" : "tel"}
                    inputMode={inputModeFor(contact.type)}
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    error={field.state.meta.errors.length > 0 ? t(invalidKey) : undefined}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="2xl"
                  className="self-start"
                  onClick={() => onToggleDelete(contact.itemId)}
                  data-testid="patient-info-contact-delete-button"
                >
                  <Trash2 aria-hidden className="size-6" />
                  {t("self-service.check-in.patient-info.review.remove")}
                </Button>
              </div>
            )}
          </form.Field>
        ),
      )}

      {additions.map((addition) => (
        <div
          key={addition.clientItemId}
          className="flex min-w-0 flex-col gap-3 rounded-2xl border border-dashed p-4 md:flex-row md:items-center"
          data-testid="patient-info-staged-addition"
        >
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-base text-foreground">
              {t(`self-service.check-in.patient-info.review.type.${addition.type}`)}
              {" · "}
              {t("self-service.check-in.patient-info.review.chip-will-add")}
            </span>
            {/* Masked like every other value on this shared screen (KC-56). */}
            <span className="text-xl break-all" data-testid="patient-info-addition-value">
              {maskValue(addition.value)}
            </span>
          </span>
          <Button
            type="button"
            variant="outline"
            size="2xl"
            onClick={() => onDiscardAddition(addition.clientItemId)}
            data-testid="patient-info-addition-remove-button"
          >
            <Trash2 aria-hidden className="size-6" />
            {t("self-service.check-in.patient-info.review.remove")}
          </Button>
        </div>
      ))}

      {guarded.map((contact) => (
        <ProtectedProposalRow
          key={contact.itemId}
          contact={contact}
          form={form}
          onDiscard={() => onDiscardProposal(contact.itemId)}
        />
      ))}

      <AddContactSlot
        allowedTypes={ADDABLE_TYPES.filter(
          (type) => eligibleContactKind(type) === category,
        )}
        category={category}
        onAdd={onAdd}
      />
    </div>
  );
}

/**
 * A contact staged for removal, with its raw value withheld.
 *
 * The value disappears rather than being struck through: once the patient has decided to remove a
 * number, continuing to print it on a waiting-room screen serves nobody. The removal stays
 * reversible until the commit.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <StagedRemovalRow contact={contact} onUndo={() => {}} />
 * ```
 */
export function StagedRemovalRow({
  contact,
  onUndo,
}: {
  contact: ReviewContact;
  onUndo: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="flex min-w-0 flex-col gap-3 md:flex-row md:items-center"
      data-testid="patient-info-staged-removal"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1 rounded-2xl border border-dashed px-4 py-3">
        <span className="text-base text-foreground">
          {t(`self-service.check-in.patient-info.review.type.${contact.type}`)}
        </span>
        <span className="text-lg text-foreground">
          {t("self-service.check-in.patient-info.review.staged-removal")}
        </span>
      </span>
      <Button
        type="button"
        variant="outline"
        size="2xl"
        onClick={onUndo}
        data-testid="patient-info-contact-undo-delete-button"
      >
        <RotateCcw className="size-6" aria-hidden="true" />
        {t("self-service.check-in.patient-info.review.undo")}
      </Button>
    </div>
  );
}

/**
 * The add-a-contact affordance: a full secondary button that opens one row, not a permanently
 * visible form. The type is a large touch toggle instead of a native select (KC-60), and
 * "Hinzufügen" stays inert until the value passes the contract's format assertion, so the patient
 * cannot queue something the commit would silently drop.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <AddContactSlot allowedTypes={["MOBILEPRIV"]} category="phone" onAdd={() => {}} />
 * ```
 */
export function AddContactSlot({
  allowedTypes,
  category,
  onAdd,
}: {
  allowedTypes: readonly EligibleContactType[];
  category: "phone" | "email";
  onAdd: (type: EligibleContactType, value: string) => void;
}) {
  const { t } = useTranslation();
  const valueId = useId();
  const [open, setOpen] = useState(false);
  const defaultType = allowedTypes[0];
  const form = useForm({
    defaultValues: { type: defaultType ?? "MOBILEPRIV", value: "" },
    onSubmit: ({ value, formApi }) => {
      onAdd(value.type, value.value);
      formApi.reset();
      setOpen(false);
    },
  });
  const ready = useStore(form.store, (state) => state.canSubmit && state.isDirty);
  const selectedType = useStore(form.store, (state) => state.values.type);

  if (defaultType === undefined) return null;

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="2xl"
        className="w-fit"
        onClick={() => setOpen(true)}
        data-testid="patient-info-add-open-button"
      >
        <Plus className="size-6" aria-hidden="true" />
        {t(`self-service.check-in.patient-info.review.add-more-${category}`)}
      </Button>
    );
  }

  return (
    <div
      className="flex min-w-0 flex-col gap-4 rounded-2xl border p-4"
      data-testid="patient-info-add-slot"
    >
      <form.Field name="type">
        {(field) => (
          <div data-testid="patient-info-add-type-select" data-value={field.state.value}>
            <CheckInChoiceGroup
              legend={t("self-service.check-in.patient-info.review.add-type")}
              options={allowedTypes.map((type) => ({
                value: type,
                label: t(`self-service.check-in.patient-info.review.type.${type}`),
                icon: contactTypeIcon(type),
                testId: `patient-info-add-type-${type.toLowerCase()}`,
              }))}
              value={field.state.value}
              onChange={(type) => field.handleChange(type)}
            />
          </div>
        )}
      </form.Field>
      <form.Field
        name="value"
        validators={{ onChange: Schema.toStandardSchemaV1(ContactAssertion) }}
      >
        {(field) => (
          <KioskTextField
            id={`patient-info-add-value-${valueId}`}
            testId="patient-info-add-value-input"
            label={t(`self-service.check-in.patient-info.review.type.${selectedType}`)}
            icon={contactTypeIcon(selectedType)}
            type={inputModeFor(selectedType) === "email" ? "email" : "tel"}
            inputMode={inputModeFor(selectedType)}
            placeholder={t(
              inputModeFor(selectedType) === "email"
                ? "self-service.check-in.patient-info.review.example-email"
                : "self-service.check-in.patient-info.review.example-phone",
            )}
            value={field.state.value}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
          />
        )}
      </form.Field>
      <Button
        type="button"
        variant="outline"
        size="2xl"
        className="w-fit"
        disabled={!ready}
        onClick={() => void form.handleSubmit()}
        data-testid="patient-info-add-button"
      >
        <Plus className="size-6" aria-hidden="true" />
        {t("self-service.check-in.patient-info.review.add")}
      </Button>
    </div>
  );
}
